/**
 * Snap-to-grid and alignment guide utilities for dragging/resizing
 * bubbles and SFX items within a cell.
 */

const SNAP_THRESHOLD = 6;

/**
 * Round a value to the nearest grid multiple.
 */
export function snapToGrid(value, gridSize) {
  if (!gridSize || gridSize <= 0) return value;
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Compute snapped position and alignment guides for a rect being dragged/resized.
 *
 * @param {Object} opts
 * @param {{ x: number, y: number, width: number, height: number }} opts.rect - proposed rect
 * @param {{ width: number, height: number }} opts.cellSize - cell dimensions
 * @param {Array<{ x: number, y: number, width: number, height: number }>} opts.siblings - other item rects
 * @param {number} opts.gridSize - grid snap size (e.g. 10)
 * @param {boolean} opts.skipSnap - if true (Alt key), return rect unchanged
 * @returns {{ snappedRect: object, guides: Array<{ axis: "x"|"y", position: number }> }}
 */
export function computeSnap({ rect, cellSize, siblings = [], gridSize = 10, skipSnap = false }) {
  if (skipSnap) {
    return { snappedRect: rect, guides: [] };
  }

  const guides = [];
  let sx = rect.x;
  let sy = rect.y;

  // Candidate snap lines
  const xCandidates = [
    0,                            // left edge
    cellSize.width,               // right edge
    cellSize.width / 2,           // center
  ];
  const yCandidates = [
    0,                            // top edge
    cellSize.height,              // bottom edge
    cellSize.height / 2,          // center
  ];

  // Add sibling edges and centers
  for (const sib of siblings) {
    xCandidates.push(sib.x, sib.x + sib.width, sib.x + sib.width / 2);
    yCandidates.push(sib.y, sib.y + sib.height, sib.y + sib.height / 2);
  }

  // Rect reference points: left, right, center
  const rectXPoints = [rect.x, rect.x + rect.width, rect.x + rect.width / 2];
  const rectYPoints = [rect.y, rect.y + rect.height, rect.y + rect.height / 2];

  let bestDx = Infinity;
  let bestDy = Infinity;
  let snapDx = 0;
  let snapDy = 0;
  const matchedX = [];
  const matchedY = [];

  // Find closest x alignment
  for (const candidate of xCandidates) {
    for (const rp of rectXPoints) {
      const dist = Math.abs(rp - candidate);
      if (dist < SNAP_THRESHOLD && dist < bestDx) {
        bestDx = dist;
        snapDx = candidate - rp;
        matchedX.length = 0;
        matchedX.push(candidate);
      } else if (dist < SNAP_THRESHOLD && dist === bestDx) {
        matchedX.push(candidate);
      }
    }
  }

  // Find closest y alignment
  for (const candidate of yCandidates) {
    for (const rp of rectYPoints) {
      const dist = Math.abs(rp - candidate);
      if (dist < SNAP_THRESHOLD && dist < bestDy) {
        bestDy = dist;
        snapDy = candidate - rp;
        matchedY.length = 0;
        matchedY.push(candidate);
      } else if (dist < SNAP_THRESHOLD && dist === bestDy) {
        matchedY.push(candidate);
      }
    }
  }

  if (matchedX.length > 0) {
    sx = rect.x + snapDx;
    for (const pos of matchedX) {
      guides.push({ axis: "x", position: pos });
    }
  } else {
    // Fall back to grid snap
    sx = snapToGrid(rect.x, gridSize);
  }

  if (matchedY.length > 0) {
    sy = rect.y + snapDy;
    for (const pos of matchedY) {
      guides.push({ axis: "y", position: pos });
    }
  } else {
    sy = snapToGrid(rect.y, gridSize);
  }

  return {
    snappedRect: { ...rect, x: sx, y: sy },
    guides,
  };
}
